import React from 'react';

// IT-themed SVG illustrations matching the site's blue/navy color scheme
export const ITThematicImages = {
  // Cloud infrastructure visualization
  cloudInfrastructure: (
    <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="cloudGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#2B3A4D" stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="serverGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#1E2A38" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      
      {/* Cloud shapes */}
      <ellipse cx="200" cy="150" rx="120" ry="60" fill="url(#cloudGrad)" opacity="0.7" />
      <ellipse cx="600" cy="200" rx="140" ry="70" fill="url(#cloudGrad)" opacity="0.6" />
      <ellipse cx="400" cy="100" rx="100" ry="50" fill="url(#cloudGrad)" opacity="0.5" />
      
      {/* Server racks */}
      <rect x="150" y="300" width="120" height="200" fill="url(#serverGrad)" rx="4" />
      <rect x="300" y="280" width="120" height="220" fill="url(#serverGrad)" rx="4" />
      <rect x="450" y="310" width="120" height="190" fill="url(#serverGrad)" rx="4" />
      
      {/* Server indicators */}
      {[0, 1, 2, 3, 4].map(i => (
        <React.Fragment key={i}>
          <rect x="160" y={320 + i * 35} width="100" height="20" fill="#0066CC" opacity="0.6" rx="2" />
          <rect x="310" y={300 + i * 40} width="100" height="20" fill="#0066CC" opacity="0.6" rx="2" />
          <rect x="460" y={330 + i * 35} width="100" height="20" fill="#0066CC" opacity="0.6" rx="2" />
        </React.Fragment>
      ))}
      
      {/* Connection lines */}
      <path d="M 210 150 Q 300 250 300 280" stroke="#0066CC" strokeWidth="2" fill="none" opacity="0.4" strokeDasharray="5,5" />
      <path d="M 600 200 Q 500 300 450 310" stroke="#0066CC" strokeWidth="2" fill="none" opacity="0.4" strokeDasharray="5,5" />
      <path d="M 400 100 Q 400 250 400 280" stroke="#0066CC" strokeWidth="2" fill="none" opacity="0.4" strokeDasharray="5,5" />
    </svg>
  ),

  // Data analytics visualization
  dataAnalytics: (
    <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="dataGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#2B3A4D" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#0066CC" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      
      {/* Data nodes */}
      {[0, 1, 2, 3, 4, 5, 6].map((i, idx) => {
        const angle = (idx * 360) / 7;
        const radius = 200;
        const cx = 400 + radius * Math.cos((angle * Math.PI) / 180);
        const cy = 300 + radius * Math.sin((angle * Math.PI) / 180);
        return (
          <React.Fragment key={i}>
            <circle cx={cx} cy={cy} r="30" fill="url(#dataGrad)" opacity="0.8" />
            <circle cx={cx} cy={cy} r="20" fill="#0066CC" opacity="0.6" />
            <circle cx={cx} cy={cy} r="8" fill="#FFFFFF" />
            {/* Connection to center */}
            <line x1="400" y1="300" x2={cx} y2={cy} stroke="#0066CC" strokeWidth="2" opacity="0.3" />
          </React.Fragment>
        );
      })}
      
      {/* Center hub */}
      <circle cx="400" cy="300" r="50" fill="url(#dataGrad)" opacity="0.9" />
      <circle cx="400" cy="300" r="35" fill="#0066CC" opacity="0.7" />
      <circle cx="400" cy="300" r="15" fill="#FFFFFF" />
      
      {/* Data flow lines */}
      {[0, 1, 2, 3, 4, 5, 6].map((i, idx) => {
        const angle1 = (idx * 360) / 7;
        const angle2 = ((idx + 1) * 360) / 7;
        const radius = 200;
        const x1 = 400 + radius * Math.cos((angle1 * Math.PI) / 180);
        const y1 = 300 + radius * Math.sin((angle1 * Math.PI) / 180);
        const x2 = 400 + radius * Math.cos((angle2 * Math.PI) / 180);
        const y2 = 300 + radius * Math.sin((angle2 * Math.PI) / 180);
        return (
          <path
            key={`flow-${i}`}
            d={`M ${x1} ${y1} Q ${400} ${300} ${x2} ${y2}`}
            stroke="#0066CC"
            strokeWidth="1.5"
            fill="none"
            opacity="0.2"
            strokeDasharray="3,3"
          />
        );
      })}
    </svg>
  ),

  // Software development visualization
  softwareDevelopment: (
    <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="codeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#2B3A4D" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      
      {/* Code blocks */}
      <rect x="100" y="100" width="200" height="150" fill="url(#codeGrad)" opacity="0.7" rx="8" />
      <rect x="100" y="110" width="180" height="8" fill="#FFFFFF" opacity="0.9" rx="2" />
      <rect x="100" y="130" width="150" height="8" fill="#FFFFFF" opacity="0.7" rx="2" />
      <rect x="100" y="150" width="170" height="8" fill="#FFFFFF" opacity="0.7" rx="2" />
      <rect x="100" y="170" width="140" height="8" fill="#FFFFFF" opacity="0.6" rx="2" />
      <rect x="100" y="190" width="160" height="8" fill="#FFFFFF" opacity="0.6" rx="2" />
      <rect x="100" y="210" width="120" height="8" fill="#FFFFFF" opacity="0.5" rx="2" />
      
      <rect x="500" y="150" width="200" height="150" fill="url(#codeGrad)" opacity="0.7" rx="8" />
      <rect x="500" y="160" width="180" height="8" fill="#FFFFFF" opacity="0.9" rx="2" />
      <rect x="500" y="180" width="160" height="8" fill="#FFFFFF" opacity="0.7" rx="2" />
      <rect x="500" y="200" width="170" height="8" fill="#FFFFFF" opacity="0.7" rx="2" />
      <rect x="500" y="220" width="150" height="8" fill="#FFFFFF" opacity="0.6" rx="2" />
      <rect x="500" y="240" width="165" height="8" fill="#FFFFFF" opacity="0.6" rx="2" />
      <rect x="500" y="260" width="130" height="8" fill="#FFFFFF" opacity="0.5" rx="2" />
      
      {/* Connection arrows */}
      <path d="M 300 175 L 500 225" stroke="#0066CC" strokeWidth="3" fill="none" opacity="0.6" markerEnd="url(#arrowhead)" />
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#0066CC" opacity="0.6" />
        </marker>
      </defs>
      
      {/* API endpoints */}
      <circle cx="400" cy="400" r="40" fill="url(#codeGrad)" opacity="0.8" />
      <circle cx="400" cy="400" r="25" fill="#0066CC" opacity="0.6" />
      <text x="400" y="407" textAnchor="middle" fill="#FFFFFF" fontSize="16" fontWeight="bold">API</text>
      
      {/* Connection lines to API */}
      <line x1="200" y1="250" x2="380" y2="380" stroke="#0066CC" strokeWidth="2" opacity="0.4" strokeDasharray="5,5" />
      <line x1="600" y1="250" x2="420" y2="380" stroke="#0066CC" strokeWidth="2" opacity="0.4" strokeDasharray="5,5" />
    </svg>
  ),

  // IT Consulting visualization
  itConsulting: (
    <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="consultGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0066CC" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#2B3A4D" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      
      {/* Central hub */}
      <circle cx="400" cy="300" r="80" fill="url(#consultGrad)" opacity="0.8" />
      <circle cx="400" cy="300" r="60" fill="#0066CC" opacity="0.6" />
      <circle cx="400" cy="300" r="30" fill="#FFFFFF" opacity="0.9" />
      
      {/* Strategy nodes */}
      {[
        { x: 200, y: 150, label: 'Strategy' },
        { x: 600, y: 150, label: 'Architecture' },
        { x: 200, y: 450, label: 'Implementation' },
        { x: 600, y: 450, label: 'Support' },
      ].map((node, idx) => (
        <React.Fragment key={idx}>
          <rect x={node.x - 60} y={node.y - 40} width="120" height="80" fill="url(#consultGrad)" opacity="0.7" rx="8" />
          <circle cx={node.x} cy={node.y} r="25" fill="#0066CC" opacity="0.8" />
          <text x={node.x} y={node.y + 5} textAnchor="middle" fill="#FFFFFF" fontSize="12" fontWeight="600">
            {String.fromCharCode(65 + idx)}
          </text>
          
          {/* Connection lines */}
          <path
            d={`M ${node.x} ${node.y} Q ${400} ${300} 400 300`}
            stroke="#0066CC"
            strokeWidth="2"
            fill="none"
            opacity="0.4"
            strokeDasharray="5,5"
          />
        </React.Fragment>
      ))}
      
      {/* Flow indicators */}
      <circle cx="400" cy="300" r="100" fill="none" stroke="#0066CC" strokeWidth="2" opacity="0.3" strokeDasharray="10,5" />
    </svg>
  ),
};

export default ITThematicImages;
